import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL } from './Config';
import './YouTubeDetailsPage.css';

function YouTubeDetailsPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [project, setProject] = useState(null);
  const [youtubeDetails, setYoutubeDetails] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('original');
  const [availableLanguages, setAvailableLanguages] = useState(['original']);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingThumbnail, setIsGeneratingThumbnail] = useState(false);
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [isGeneratingVisualPrompt, setIsGeneratingVisualPrompt] = useState(false);
  const [additionalPrompt, setAdditionalPrompt] = useState('');
  const [thumbnails, setThumbnails] = useState([]);
  const [message, setMessage] = useState({ text: '', type: '' });
  
  // Thumbnail generation state
  const [visualPrompt, setVisualPrompt] = useState('');
  const [referenceImages, setReferenceImages] = useState([]);
  const [showThumbnailSection, setShowThumbnailSection] = useState(false);

  useEffect(() => {
    fetchProject();
    fetchYoutubeDetails();
    fetchThumbnails();
  }, [projectId, selectedLanguage]);

  const fetchProject = async () => {
    try {
      const response = await fetch(`${API_URL}/projects/${projectId}`);
      if (response.ok) {
        const data = await response.json();
        setProject(data);
        
        // Extract available languages from the project data
        if (data.availableLanguages) {
          setAvailableLanguages(['original', ...data.availableLanguages]);
        }
      }
    } catch (error) {
      console.error('Error fetching project:', error);
      showMessage('Error loading project data', 'error');
    }
  };

  const fetchYoutubeDetails = async () => {
    try {
      const response = await fetch(
        `${API_URL}/youtube/projects/${projectId}/details?target_language_code=${selectedLanguage}`
      );
      if (response.ok) {
        const data = await response.json();
        setYoutubeDetails(data.exists ? data : null);
      }
    } catch (error) {
      console.error('Error fetching YouTube details:', error);
    }
  };

  const fetchThumbnails = async () => {
    try {
      const response = await fetch(`${API_URL}/youtube/projects/${projectId}/thumbnails`);
      if (response.ok) {
        const data = await response.json();
        setThumbnails(data);
      }
    } catch (error) {
      console.error('Error fetching thumbnails:', error);
    }
  };

  const generatePromptSuggestion = async () => {
    setIsGeneratingPrompt(true);
    try {
      const response = await fetch(
        `${API_URL}/youtube/projects/${projectId}/generate-prompt-suggestion`,
        { method: 'POST' }
      );

      if (response.ok) {
        const data = await response.json();
        setAdditionalPrompt(data.suggestion);
        showMessage('Prompt suggestion generated!', 'success');
      } else {
        const error = await response.json();
        showMessage(`Error: ${error.detail}`, 'error');
      }
    } catch (error) {
      console.error('Error generating prompt suggestion:', error);
      showMessage('Error generating prompt suggestion', 'error');
    } finally {
      setIsGeneratingPrompt(false);
    }
  };

  const generateVisualPromptSuggestion = async () => {
    setIsGeneratingVisualPrompt(true);
    try {
      const response = await fetch(
        `${API_URL}/youtube/projects/${projectId}/generate-visual-prompt-suggestion`,
        { method: 'POST' }
      );

      if (response.ok) {
        const data = await response.json();
        setVisualPrompt(data.suggestion);
        showMessage('Visual prompt suggestion generated!', 'success');
      } else {
        const error = await response.json();
        showMessage(`Error: ${error.detail}`, 'error');
      }
    } catch (error) {
      console.error('Error generating visual prompt suggestion:', error);
      showMessage('Error generating visual prompt suggestion', 'error');
    } finally {
      setIsGeneratingVisualPrompt(false);
    }
  };

  const generateYoutubeDetails = async () => {
    setIsGenerating(true);
    try {
      const params = new URLSearchParams({
        target_language_code: selectedLanguage,
        force_regenerate: 'true'
      });
      
      if (additionalPrompt.trim()) {
        params.append('additional_prompt', additionalPrompt.trim());
      }

      const response = await fetch(
        `${API_URL}/youtube/projects/${projectId}/generate-details?${params}`,
        { method: 'POST' }
      );

      if (response.ok) {
        const data = await response.json();
        setYoutubeDetails(data);
        showMessage('YouTube details generated successfully!', 'success');
      } else {
        const error = await response.json();
        showMessage(`Error: ${error.detail}`, 'error');
      }
    } catch (error) {
      console.error('Error generating YouTube details:', error);
      showMessage('Error generating YouTube details', 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    setReferenceImages(files);
  };

  const generateThumbnail = async () => {
    if (!youtubeDetails || !visualPrompt.trim()) {
      showMessage('Please generate YouTube details first and provide visual requirements', 'error');
      return;
    }

    setIsGeneratingThumbnail(true);
    try {
      const formData = new FormData();
      formData.append('youtube_title', youtubeDetails.title);
      formData.append('youtube_description', youtubeDetails.description);
      formData.append('visual_prompt', visualPrompt.trim());
      formData.append('language_code', selectedLanguage);
      
      // Add reference images if provided
      if (referenceImages.length > 0) {
        referenceImages.forEach((image, index) => {
          formData.append('reference_images', image);
        });
      }

      const response = await fetch(
        `${API_URL}/youtube/projects/${projectId}/generate-thumbnail`,
        {
          method: 'POST',
          body: formData
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          showMessage('Thumbnail generated successfully!', 'success');
          await fetchThumbnails(); // Refresh thumbnails list
        } else {
          showMessage(`Thumbnail generation failed: ${data.error}`, 'error');
        }
      } else {
        const error = await response.json();
        showMessage(`Error: ${error.detail}`, 'error');
      }
    } catch (error) {
      console.error('Error generating thumbnail:', error);
      showMessage('Error generating thumbnail', 'error');
    } finally {
      setIsGeneratingThumbnail(false);
    }
  };

  const deleteThumbnail = async (filename) => {
    if (!window.confirm('Are you sure you want to delete this thumbnail?')) {
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/youtube/projects/${projectId}/thumbnails/${encodeURIComponent(filename)}`,
        { method: 'DELETE' }
      );

      if (response.ok) {
        showMessage('Thumbnail deleted successfully', 'success');
        await fetchThumbnails();
      } else {
        const error = await response.json();
        showMessage(`Error: ${error.detail}`, 'error');
      }
    } catch (error) {
      console.error('Error deleting thumbnail:', error);
      showMessage('Error deleting thumbnail', 'error');
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  const getLanguageDisplayName = (code) => {
    const languageNames = {
      original: 'English (Original)',
      es: 'Spanish',
      fr: 'French',
      de: 'German',
      zh: 'Chinese',
      ja: 'Japanese',
      ar: 'Arabic',
      hi: 'Hindi',
      pt: 'Portuguese',
      ru: 'Russian',
      it: 'Italian',
      ko: 'Korean',
      nl: 'Dutch',
      sv: 'Swedish',
      tr: 'Turkish'
    };
    return languageNames[code] || code.toUpperCase();
  };

  if (!project) {
    return (
      <div className="youtube-details-page">
        <div className="loading">Loading project...</div>
      </div>
    );
  }

  return (
    <div className="youtube-details-page">
      <header className="page-header">
        <button onClick={() => navigate(`/project/${projectId}`)} className="back-button">
          ← Back to Project
        </button>
        <h1>YouTube Details & Thumbnails</h1>
        <p className="project-title">{project.title}</p>
      </header>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="content-wrapper">
        {/* Language Selection */}
        <section className="language-section">
          <h2>Language Selection</h2>
          <select 
            value={selectedLanguage} 
            onChange={(e) => setSelectedLanguage(e.target.value)}
            className="language-select"
          >
            {availableLanguages.map(lang => (
              <option key={lang} value={lang}>
                {getLanguageDisplayName(lang)}
              </option>
            ))}
          </select>
        </section>

        {/* YouTube Details Generation */}
        <section className="youtube-details-section">
          <h2>YouTube Details</h2>
          
          <div className="generation-controls">
            <div className="prompt-input">
              <label>Additional Requirements (Optional):</label>
              <div className="prompt-input-wrapper">
                <textarea
                  value={additionalPrompt}
                  onChange={(e) => setAdditionalPrompt(e.target.value)}
                  placeholder="e.g., Focus on beginner audience, include specific keywords, emphasize practical benefits..."
                  rows="3"
                />
                <button 
                  onClick={generatePromptSuggestion} 
                  disabled={isGeneratingPrompt}
                  className="generate-prompt-button"
                  title="Generate AI-powered prompt suggestions"
                >
                  {isGeneratingPrompt ? 'Generating...' : 'Generate Prompt'}
                </button>
              </div>
            </div>
            
            <button 
              onClick={generateYoutubeDetails} 
              disabled={isGenerating}
              className="generate-button"
            >
              {isGenerating ? 'Generating...' : youtubeDetails ? 'Regenerate Details' : 'Generate Details'}
            </button>
          </div>

          {youtubeDetails && (
            <div className="youtube-details-display">
              <h3>Generated YouTube Details</h3>
              
              <div className="detail-item">
                <label>Title ({youtubeDetails.title?.length || 0}/60 characters):</label>
                <div className="title-display">{youtubeDetails.title}</div>
              </div>
              
              <div className="detail-item">
                <label>Description:</label>
                <div className="description-display">{youtubeDetails.description}</div>
              </div>
              
              <div className="detail-meta">
                <small>
                  Language: {getLanguageDisplayName(youtubeDetails.language_code)} | 
                  Created: {new Date(youtubeDetails.created_at).toLocaleString()}
                  {youtubeDetails.updated_at && youtubeDetails.updated_at !== youtubeDetails.created_at && (
                    <> | Updated: {new Date(youtubeDetails.updated_at).toLocaleString()}</>
                  )}
                </small>
              </div>

              <button 
                onClick={() => setShowThumbnailSection(!showThumbnailSection)}
                className="toggle-thumbnail-button"
              >
                {showThumbnailSection ? 'Hide' : 'Show'} Thumbnail Generation
              </button>
            </div>
          )}
        </section>

        {/* Thumbnail Generation Section */}
        {showThumbnailSection && youtubeDetails && (
          <section className="thumbnail-section">
            <h2>Generate Thumbnail</h2>
            
            <div className="thumbnail-generation">
              <div className="upload-section">
                <label>Reference Images (Optional):</label>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="file-input"
                />
                <p className="upload-hint">
                  Optionally upload 1-5 reference images that represent the style you want for your thumbnail. If no images are provided, the thumbnail will be generated based on your visual requirements alone.
                </p>
                
                {referenceImages.length > 0 && (
                  <div className="selected-images">
                    <p>{referenceImages.length} image(s) selected:</p>
                    <ul>
                      {Array.from(referenceImages).map((file, index) => (
                        <li key={index}>{file.name}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="visual-prompt-section">
                <label>Visual Requirements:</label>
                <div className="prompt-input-wrapper">
                  <textarea
                    value={visualPrompt}
                    onChange={(e) => setVisualPrompt(e.target.value)}
                    placeholder="Describe the visual style, colors, layout, and elements you want in the thumbnail. Example: 'Modern tech style with blue and white colors, include code snippets, professional look with clear readable text...'"
                    rows="4"
                  />
                  <button 
                    onClick={generateVisualPromptSuggestion} 
                    disabled={isGeneratingVisualPrompt}
                    className="generate-prompt-button"
                    title="Generate AI-powered visual prompt suggestions"
                  >
                    {isGeneratingVisualPrompt ? 'Generating...' : 'Generate Prompt'}
                  </button>
                </div>
              </div>

              <button 
                onClick={generateThumbnail} 
                disabled={isGeneratingThumbnail || !visualPrompt.trim()}
                className="generate-thumbnail-button"
              >
                {isGeneratingThumbnail ? 'Generating Thumbnail...' : 'Generate Thumbnail'}
              </button>
            </div>
          </section>
        )}

        {/* Thumbnails List */}
        <section className="thumbnails-list-section">
          <h2>Generated Thumbnails</h2>
          
          {thumbnails.length === 0 ? (
            <p className="no-thumbnails">No thumbnails generated yet.</p>
          ) : (
            <div className="thumbnails-grid">
              {thumbnails.map((thumbnail, index) => (
                <div key={index} className="thumbnail-item">
                  <div className="thumbnail-info">
                    <h4>Thumbnail {index + 1}</h4>
                    <p>Language: {getLanguageDisplayName(thumbnail.language)}</p>
                    <p>Created: {new Date(thumbnail.created_at).toLocaleString()}</p>
                    <p>Size: {(thumbnail.size / 1024).toFixed(1)} KB</p>
                  </div>
                  
                  <div className="thumbnail-actions">
                    <a 
                      href={`${API_URL}/youtube${thumbnail.path}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="view-button"
                    >
                      View
                    </a>
                    <button 
                      onClick={() => deleteThumbnail(thumbnail.filename)}
                      className="delete-button"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default YouTubeDetailsPage;